import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { isDefined } from '@follow/cdk'
import { useLocalStorage } from '../../../../hooks/utils'
import { FwTrackingEvent, withTrackingEvent } from '../../../../misc/Tracking'
import styles from './PatientGeneralInfos.module.scss'
import { PatientInfosItem } from '../PatientInfosItem'
import { PatientInfosShowMore } from '../PatientInfosShowMore'
import { Sex } from '../../../../model/Patient'
import { SEX_FORM_LABELS, TITLE_FORM_LABELS } from '../../PatientForm/PatientForm.model'
import { Icon, IconButton, TooltipWrapper } from '../../../shared'
import { ColorsV2 } from '../../../../design-system/colors_v2'
import { inputInseeNumberFormatter } from '../../../../misc/inseeNumber.utils'
import { PatientGeneralInfosProps } from './PatientGeneralInfos.model'
import {DATE_FORMAT_VERBOSE_ALTERNATIVE, formatStringFr} from '../../../../misc/date.utilities'
import { ContactBottomPanel } from '../../../shared/bottomPanel/ContactBottomPanel'

const withTrackingAccordionEvent = (section: string) =>
  withTrackingEvent(FwTrackingEvent.TOGGLE_PATIENT_INFO_ACCORDION, {
    section,
  })

export const PatientGeneralInfosComponent: FunctionComponent<PatientGeneralInfosProps> = ({
  patient,
  copyTextToClipboard, addError

}) => {
  const [showingMore, setShowingMore] = useLocalStorage<boolean>('ui.toggleShowMore.open', false)
  const [copyINSIcon, setCopyINSIcon] = useState<'copy' | 'check'>('copy')
  const [contactId, setContactId] = useState<string | null>(null)

  const setShowingMoreInfos = withTrackingAccordionEvent('Voir plus')(setShowingMore)

  const timerId = useRef<NodeJS.Timeout | null>(null)

  const handleCloseContactBottomPanel = useCallback(() => {
    setContactId(null)
  }, [])

  const handleDisplayContact = useCallback(
    (id: string) => () => {
      setContactId(id)
    },
    [],
  )

  useEffect(() => {
    return () => {
      if (timerId.current) clearTimeout(timerId.current)
    }
  }, [timerId])
  const handleCopyINSWithoutSpace = () => {
    copyTextToClipboard(patient.inseeNumber || '')
    setCopyINSIcon('check')
    timerId.current = setTimeout(() => {
      setCopyINSIcon('copy')
    }, 2000)
  }

  return (
    <>
      {patient.title && (
        <PatientInfosItem label={'Civilité'} value={TITLE_FORM_LABELS[patient.title]} />
      )}
      {patient.usedLastName && (
        <PatientInfosItem label={'Nom utilisé'} value={patient.usedLastName} />
      )}
      {patient.usedFirstName && (
        <PatientInfosItem label={'Prénom utilisé'} value={patient.usedFirstName} />
      )}
      {patient.birthLastName && (
        <PatientInfosItem label={'Nom de naissance'} value={patient.birthLastName} />
      )}
      {patient.birthFirstName && (
        <PatientInfosItem label={'Premier prénom de naissance'} value={patient.birthFirstName} />
      )}
      {patient.birthDate && (
        <PatientInfosItem
          label="Date de naissance"
          value={formatStringFr(patient.birthDate, DATE_FORMAT_VERBOSE_ALTERNATIVE)}
        />
      )}
      {patient.sex && (
        <PatientInfosItem
          label={'Sexe'}
          value={
            <div className={styles.sex}>
              <span className={styles.gender}>{SEX_FORM_LABELS[patient.sex]}</span>
              {patient.sex !== Sex.UNKNOWN && (
                <Icon
                  icon={patient.sex === Sex.MALE ? 'male' : 'female'}
                  color={ColorsV2.contentDark1}
                  size="nano"
                />
              )}
            </div>
          }
        />
      )}
      {patient.profession && <PatientInfosItem label={'Profession'} value={patient.profession} />}
      {patient.contacts.map((contact, key) => (
        <div className={styles.contact} key={key}>
          {contact.assignedDoctor ? (
            <span>Médecin Traitant</span>
          ) : contact.addressingDoctor ? (
            <span>Adressant</span>
          ) : (
            <span>{contact.profession?.description}</span>
          )}
          <span className={styles.infos} onClick={handleDisplayContact(contact.id)}>
            {contact.familyName} {contact.firstName}
          </span>
        </div>
      ))}
      {showingMore && (
        <>
          {patient.otherFirstNames && (
            <PatientInfosItem
              label={'Autres prénoms de naissance'}
              value={patient.otherFirstNames}
            />
          )}
          {patient.serial && <PatientInfosItem label={'Matricule'} value={patient.serial} />}
          {patient.birthPlaceCode && (
            <PatientInfosItem label={'Code lieu de naissance'} value={patient.birthPlaceCode} />
          )}
          {patient.address?.street2 && (
            <PatientInfosItem label={"Complément d'adresse"} value={patient.address.street2} />
          )}
          {patient.address?.street && (
            <PatientInfosItem label={'Rue'} value={patient.address.street} />
          )}
          {patient.address?.zipCode && (
            <PatientInfosItem label={'Code postal'} value={patient.address.zipCode} />
          )}
          {patient.address?.city && (
            <PatientInfosItem label={'Ville'} value={patient.address.city} />
          )}
          {patient.address?.country && (
            <PatientInfosItem label={'Pays'} value={patient.address.country} />
          )}
          {patient.telephone1 && (
            <PatientInfosItem
              label={'Téléphone 1'}
              value={
                <a className={styles.phoneNumber} href={`tel:${patient.telephone1}`}>
                  {patient.telephone1}
                </a>
              }
            />
          )}
          {patient.telephone2 && (
            <PatientInfosItem
              label={'Téléphone 2'}
              value={
                <a className={styles.phoneNumber} href={`tel:${patient.telephone2}`}>
                  {patient.telephone2}
                </a>
              }
            />
          )}
          {patient.email && <PatientInfosItem label={'Email'} value={patient.email} />}
          {patient.healthInsuranceNumber && (
            <PatientInfosItem
              label={'Assurance complémentaire'}
              value={patient.healthInsuranceNumber}
            />
          )}
          {patient.inseeNumber && (
            <PatientInfosItem
              label={'Matricule INS'}
              value={
                <div className={styles.ins}>
                  <span>{inputInseeNumberFormatter(patient.inseeNumber)}</span>
                  <TooltipWrapper content={'Copie du matricule sans espace'}>
                    <div className={styles.insIcon}>
                      <IconButton
                        onClick={handleCopyINSWithoutSpace}
                        icon={copyINSIcon}
                        size="nano"
                        theme="light"
                        testId="button-patient_infos-insee_number-copy"
                      />
                    </div>
                  </TooltipWrapper>
                </div>
              }
            />
          )}
          {patient.assigningAuthority?.label && (
            <PatientInfosItem
              label={"Identifiant d'objet (OID)"}
              value={patient.assigningAuthority.label}
            />
          )}
        </>
      )}
      {patient.customFields.map(({ label, value }, index) => (
        <PatientInfosItem key={index} label={label} value={value} />
      ))}
      <PatientInfosShowMore
        onClick={(showMore) => {
          setShowingMoreInfos(showMore)
        }}
        showingMore={showingMore}
      />
      <ContactBottomPanel
        addError={addError}
        patientId={patient.id}
        contactId={contactId}
        display={isDefined(contactId)}
        onRequestClose={handleCloseContactBottomPanel}
        readOnly
      />
    </>
  )
}

export default PatientGeneralInfosComponent
